import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import RouteMap from '../components/workout/RouteMap';
import StreamCharts, {
  ActivityInterval,
  ActivityStreamPoint,
} from '../components/StreamCharts';
import {
  dayjs,
  formatDistance,
  formatFriendlyDuration,
  formatWorkoutPace,
} from '../lib/utils';

type SharedWorkoutData = {
  title: string;
  description?: string;
  type?: string;
  distance?: number;
  movingTime?: number;
  elapsedTime?: number;
  averageHeartRate?: number;
  maxHeartRate?: number;
  elevationGain?: number;
  completedAt?: string;
  polylineEncoded?: string;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  activityStreams?: ActivityStreamPoint[];
  activityIntervals?: ActivityInterval[];
};

export default function SharedWorkout() {
  const { token } = useParams({ from: '/shared/workouts/$token' });
  const workout = useQuery<SharedWorkoutData>({
    queryKey: ['shared-workout', token],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/shared/workouts/${encodeURIComponent(token)}`,
      );
      if (!response.ok) throw new Error('Shared workout not found');
      const result = await response.json();
      return result.activity;
    },
  });

  if (workout.isLoading) {
    return (
      <div className="surface-card mx-auto m-6 max-w-md p-10 text-center text-brand-slate">
        Loading shared workout...
      </div>
    );
  }
  if (!workout.data || workout.error) {
    return (
      <div className="surface-card mx-auto m-6 max-w-md p-10 text-center text-brand-slate">
        This share link is not available.
      </div>
    );
  }

  const data = workout.data;
  const duration = data.movingTime || data.elapsedTime || 0;
  const metrics = [
    ['Distance', formatDistance(data.distance || 0)],
    ['Time', formatFriendlyDuration(duration)],
    ['Avg pace', formatWorkoutPace(data.type, data.distance || 0, duration)],
    [
      'Elevation',
      data.elevationGain
        ? `${Math.round(data.elevationGain * 3.28084)} ft`
        : 'N/A',
    ],
  ];

  return (
    <main className="mx-auto max-w-4xl space-y-6 p-4 pb-12 md:p-8">
      <header>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-slate">
          Shared from RaceTrainer
        </p>
        <h1 className="text-3xl font-bold text-brand-midnight">
          {data.title || 'Untitled workout'}
        </h1>
        <p className="mt-1 text-brand-slate">
          {data.completedAt && dayjs(data.completedAt).isValid()
            ? dayjs(data.completedAt).format('dddd, MMMM D, YYYY · h:mm A')
            : null}
        </p>
        {data.type ? (
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-midnight px-3 py-1 text-sm font-semibold capitalize text-white">
            <Activity className="h-3.5 w-3.5 text-brand-volt" />
            {data.type}
          </span>
        ) : null}
      </header>

      {data.polylineEncoded || (data.startLatitude && data.startLongitude) ? (
        <div className="surface-card rounded-2xl p-2">
          <RouteMap
            {...data}
            className="h-72 w-full overflow-hidden rounded-xl md:h-96"
            showMarkers={false}
            showAttribution={false}
          />
        </div>
      ) : null}

      <section className="surface-card grid grid-cols-2 gap-px overflow-hidden rounded-2xl md:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label} className="bg-white/60 p-5">
            <div className="text-xs font-bold uppercase tracking-wide text-brand-slate">
              {label}
            </div>
            <div className="mt-1 text-xl font-semibold text-brand-midnight">
              {value}
            </div>
          </div>
        ))}
      </section>

      {data.description ? (
        <section className="surface-card p-6">
          <h2 className="mb-2 font-bold text-brand-midnight">Workout notes</h2>
          <p className="leading-7 text-brand-slate">{data.description}</p>
        </section>
      ) : null}

      {data.activityStreams ? (
        data.activityStreams.length > 0 ? (
          <StreamCharts
            streams={data.activityStreams}
            intervals={data.activityIntervals}
          />
        ) : null
      ) : null}
    </main>
  );
}
